import { Box, Stack } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import type {
  ColDef,
  FilterChangedEvent,
  FirstDataRenderedEvent,
  GridApi
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import LoadingIndicator from '@/components/LoadingIndicator';
import { isReadOnlyManagedSourceType } from '@/features/SourceCreate/SourceCreate.constants';
import useGridTheme from '@/hooks/useGridTheme';
import { useDeviceSelectionParams } from '@/pages/Administration/Devices/Devices.hooks';
import { filteredLimaSourcesAtom } from '@/pages/Administration/Devices/Devices.store';
import {
  selectedOrganizationAtom,
  selectedOrganizationIdAtom
} from '@/pages/Organizations';

import type { DevicesTableProps } from '../DevicesTable/DevicesTable.types';
import { ActionMenuRenderer } from './CloudSourcesTable.cellRenderers';
import {
  autoGroupColumnDef,
  LIMA_CLOUD_SOURCES_COLUMN_DEFS
} from './CloudSourcesTable.constant';
import { useCloudSourceListing } from './CloudSourcesTable.hooks';
import useStyles from './CloudSourcesTable.styles';
import type { CloudSourceTreeNode } from './CloudSourcesTable.types';
import {
  buildCloudSourceTreeRows,
  getCloudSourcesRowClass,
  reconcileCloudSourcesDetailHighlight
} from './CloudSourcesTable.utils';
import CloudSourcesTableHeader from './CloudSourcesTableHeader/CloudSourcesTableHeader';
import { useCloudSourceActivation } from './hooks/useCloudSourceActivation.hook';
import { useCloudSourcesActions } from './hooks/useCloudSourcesActions.hook';
import { useCloudSourcesGrid } from './hooks/useCloudSourcesGrid.hook';
import { useCloudSourcesHighlighting } from './hooks/useCloudSourcesHighlighting.hook';
import { useCloudSourcesRowSelection } from './hooks/useCloudSourcesRowSelection.hook';

const CloudSourcesTable = ({
  closeDeviceDetail,
  detailPanelVisible,
  isMainDevicePage = true
}: DevicesTableProps) => {
  const { classes } = useStyles({ enableTimeFilter: false });
  const [selectedOrgData] = useAtom(selectedOrganizationAtom);
  const [filteredLimaSources] = useAtom(filteredLimaSourcesAtom);
  const [selectedOrganizationId] = useAtom(selectedOrganizationIdAtom);
  const { sourcesQuery, sourcesActivationQuery, isLoading } =
    useCloudSourceListing();
  const { sourceUid } = useDeviceSelectionParams();
  const queryClient = useQueryClient();
  const gridTheme = useGridTheme();
  const gridRef = useRef<AgGridReact<CloudSourceTreeNode>>(null);

  // Use extracted hooks for focused concerns
  const grid = useCloudSourcesGrid();
  const selection = useCloudSourcesRowSelection();
  const activation = useCloudSourceActivation();
  const actions = useCloudSourcesActions(async (toggleState) => {
    await activation.handleSingleSourceToggle(toggleState);
  });
  const highlighting = useCloudSourcesHighlighting();

  // Transform filtered data to tree rows and derive row-id lookup set.
  const { treeData, filteredTreeRowIds } = useMemo(() => {
    const rows = buildCloudSourceTreeRows(filteredLimaSources, {
      selectedOrganizationId: selectedOrganizationId as number,
      includeMicrosoftProviders: true
    });

    const normalizedRows = rows.map((row) => {
      const actionSource =
        row.type === 'provider' ? row.providerData : row.sourceData;

      return {
        ...row,
        rowActionModel: {
          active: actionSource?.active ?? row.active ?? false,
          resubmit: actionSource?.resubmit ?? false,
          statusCode: actionSource?.source_statuses?.[0]?.status_code ?? 0,
          sourceTypeId: actionSource?.source_type_id,
          parentSourceUid: actionSource?.parent_source_uid ?? null,
          allowSourceActivation:
            actionSource?.allowSourceActivation ?? row.allowSourceActivation
        }
      };
    });

    return {
      treeData: normalizedRows,
      filteredTreeRowIds: new Set(normalizedRows.map((node) => node.id))
    };
  }, [filteredLimaSources, selectedOrganizationId]);

  const reconcileSelectionFromUrl = useCallback(
    (api: GridApi<CloudSourceTreeNode>) => {
      reconcileCloudSourcesDetailHighlight({
        api,
        selectedRowId: highlighting.selectedSourceUid,
        filteredRowIds: filteredTreeRowIds,
        onMissingSelection: closeDeviceDetail
      });
    },
    [closeDeviceDetail, filteredTreeRowIds, highlighting.selectedSourceUid]
  );

  const refreshSources = useCallback(async () => {
    if (!sourcesQuery.isFetching && !sourcesActivationQuery.isFetching) {
      // Wait for data to load before refreshing grid
      await Promise.all([
        sourcesQuery.refetch(),
        sourcesActivationQuery.refetch()
      ]);
    }

    if (sourceUid) {
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.some(
            (part) => typeof part === 'string' && part.includes(sourceUid)
          )
      });
    }
  }, [queryClient, sourceUid, sourcesQuery, sourcesActivationQuery]);

  // Stable actionColumn definition - configuration never changes
  const actionColumn = useMemo<ColDef<CloudSourceTreeNode>>(
    () => ({
      colId: 'actions',
      headerName: '',
      width: 50,
      sortable: false,
      filter: false,
      resizable: false,
      suppressNavigable: true,
      editable: false,
      suppressHeaderMenuButton: true,
      suppressCellSelection: true,
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'default'
      },
      suppressKeyboardEvent: () => true,
      onCellClicked: (event) => {
        // Prevent row click and cell click events from propagating
        event.event?.stopPropagation();
        event.event?.preventDefault();
      },
      cellRenderer: ActionMenuRenderer
    }),
    [] // Empty deps - column config is static
  );

  // Dynamic data passed via context to avoid column reconfiguration
  // Note: Using exact property names expected by ActionMenuRenderer
  const gridContext = useMemo(
    () => ({
      hasUpdatePermission: selection.hasAdminUpdatePermission,
      selectedOrgData: selection.selectedOrgData,
      selectedSourceUid: highlighting.selectedSourceUid,
      onMenuClick: actions.handleMenuClick
    }),
    [
      selection.hasAdminUpdatePermission,
      selection.selectedOrgData,
      highlighting.selectedSourceUid,
      actions.handleMenuClick
    ]
  );

  const columnDefs = useMemo(
    () => [...LIMA_CLOUD_SOURCES_COLUMN_DEFS, actionColumn],
    [actionColumn]
  );

  // On Organization Change collapse source detail pane
  useEffect(() => {
    closeDeviceDetail();
    // closeDeviceDetail is stable (wrapped in useCallback in parent), excluded from deps
  }, [selectedOrgData?.id]);

  useEffect(() => {
    if (!gridRef.current?.api) {
      return;
    }
    reconcileSelectionFromUrl(gridRef.current.api);
  }, [reconcileSelectionFromUrl]);

  const onFirstDataRendered = useCallback(
    (event: FirstDataRenderedEvent<CloudSourceTreeNode>) => {
      reconcileSelectionFromUrl(event.api);
    },
    [reconcileSelectionFromUrl]
  );

  const onFilterChanged = useCallback(
    (event: FilterChangedEvent<CloudSourceTreeNode>) => {
      reconcileSelectionFromUrl(event.api);
    },
    [reconcileSelectionFromUrl]
  );

  return (
    <Stack direction="column" className={classes.requestsTableWrapper}>
      <Box className={classes.searchBar}>
        <CloudSourcesTableHeader
          collapsed={!detailPanelVisible}
          refreshSources={refreshSources}
          gridRef={gridRef}
          isCustomerCloudSourcePage={!isMainDevicePage}
        />
      </Box>
      <LoadingIndicator isLoading={isLoading} />
      <activation.ConfirmActivateDeactivateModal />
      <Box sx={{ height: '100%', width: '100%' }}>
        <AgGridReact
          ref={gridRef}
          className="CloudSourcesTable"
          rowData={treeData}
          columnDefs={columnDefs}
          treeData={true}
          animateRows={true}
          groupDefaultExpanded={0}
          suppressContextMenu={true}
          theme={gridTheme}
          getRowId={(params) => params.data.id}
          getDataPath={(data) => data.path}
          onFirstDataRendered={onFirstDataRendered}
          onFilterChanged={onFilterChanged}
          autoGroupColumnDef={autoGroupColumnDef}
          rowHeight={70}
          rowSelection={{
            mode: 'multiRow',
            checkboxes: (params: { data?: CloudSourceTreeNode }) =>
              params.data?.type === 'source' &&
              !isReadOnlyManagedSourceType(
                params.data?.sourceData?.source_type_id ??
                  params.data?.providerData?.source_type_id
              ),
            hideDisabledCheckboxes: true,
            groupSelects: 'self',
            headerCheckbox: true,
            enableClickSelection: false,
            selectAll: 'currentPage'
          }}
          pagination={true}
          paginationPageSize={25}
          paginationPageSizeSelector={[25, 50, 100]}
          onRowClicked={highlighting.handleRowClick}
          onCellClicked={highlighting.handleCellClicked}
          onSelectionChanged={grid.onSelectionChanged}
          suppressRowClickSelection={true}
          suppressCellFocus={true}
          isRowSelectable={selection.canSelectRow}
          getRowClass={getCloudSourcesRowClass}
          context={gridContext}
        />
      </Box>
    </Stack>
  );
};

export default CloudSourcesTable;
